import React, { useEffect, useState } from "react";
import { ArrowUpCircle, Check, Zap, Clock, CheckCircle2, XCircle } from "lucide-react";
import { portalApi, rupiah, errMsg } from "./portalApi";

const REQ_STYLE = {
  pending: ["Menunggu", "bg-amber-500/15 text-amber-300 border-amber-500/40", Clock],
  approved: ["Disetujui", "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", CheckCircle2],
  rejected: ["Ditolak", "bg-rose-500/15 text-rose-300 border-rose-500/40", XCircle],
};

export const PortalUpgradePage = () => {
  const [pakets, setPakets] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [catatan, setCatatan] = useState("");
  const [msg, setMsg] = useState({ ok: null, text: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [p, r] = await Promise.all([portalApi.get("/paket"), portalApi.get("/upgrade")]);
    setPakets(p.data);
    setReqs(r.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const submit = async () => {
    if (!selected) return;
    setLoading(true);
    setMsg({ ok: null, text: "" });
    try {
      const res = await portalApi.post("/upgrade", { paket_id: selected, catatan });
      setMsg({ ok: true, text: res.data.message });
      setSelected(null);
      setCatatan("");
      await load();
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    } finally {
      setLoading(false);
    }
  };

  const hasPending = reqs.some((r) => r.status === "pending");

  return (
    <div className="space-y-4" data-testid="portal-upgrade">
      <h1 className="text-base font-bold text-white flex items-center gap-2"><ArrowUpCircle className="w-4 h-4 text-cyan-400" /> Ajukan Perubahan Paket</h1>
      <p className="text-xs text-slate-400">Pilih paket baru. Pengajuan akan diproses oleh petugas kami dan Anda akan dihubungi via WhatsApp.</p>

      {msg.text && <div className={`p-2.5 rounded-lg text-xs border ${msg.ok ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300" : "bg-rose-500/10 border-rose-500/40 text-rose-300"}`} data-testid="portal-upgrade-msg">{msg.text}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="portal-paket-list">
        {pakets.map((p) => {
          const active = selected === p.id;
          return (
            <button key={p.id} disabled={p.current || hasPending} onClick={() => setSelected(p.id)}
              className={`text-left p-3.5 rounded-xl border transition-colors ${p.current ? "bg-slate-900 border-slate-700 opacity-70" : active ? "bg-cyan-500/10 border-cyan-500/60" : "bg-[#0d1527] border-slate-800 hover:border-slate-600"} disabled:cursor-not-allowed`}
              data-testid={`portal-paket-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-bold text-white">{p.nama}</p>
                  <p className="text-[11px] text-cyan-300 flex items-center gap-1 mt-0.5"><Zap className="w-3 h-3" /> {p.kecepatan} • {p.download_speed}/{p.upload_speed} Mbps</p>
                </div>
                {p.current ? <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-700 text-slate-200">Paket Saat Ini</span> : active && <Check className="w-5 h-5 text-cyan-400" />}
              </div>
              <p className="text-base font-bold text-amber-300 font-mono mt-2">{rupiah(p.harga)}<span className="text-[10px] text-slate-500 font-sans"> /bulan</span></p>
              {p.deskripsi && <p className="text-[11px] text-slate-500 mt-1">{p.deskripsi}</p>}
            </button>
          );
        })}
      </div>

      {selected && !hasPending && (
        <div className="p-4 rounded-2xl bg-[#0d1527] border border-cyan-500/40 space-y-3" data-testid="portal-upgrade-form">
          <textarea value={catatan} onChange={(e) => setCatatan(e.target.value)} rows={2} placeholder="Catatan (opsional), misal: mulai bulan depan"
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-sm text-white" data-testid="portal-upgrade-catatan" />
          <button onClick={submit} disabled={loading} className="w-full py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold text-sm disabled:opacity-60" data-testid="portal-upgrade-submit">
            {loading ? "Mengirim..." : "Kirim Pengajuan"}
          </button>
        </div>
      )}

      {reqs.length > 0 && (
        <div className="space-y-2" data-testid="portal-upgrade-history">
          <h2 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Riwayat Pengajuan</h2>
          {reqs.map((r) => {
            const [label, cls, Icon] = REQ_STYLE[r.status] || REQ_STYLE.pending;
            return (
              <div key={r.id} className="p-3 rounded-xl bg-[#0d1527] border border-slate-800 flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm text-white"><span className="text-slate-400">{r.paket_lama_nama || "-"}</span> → <b>{r.paket_baru_nama}</b></p>
                  <p className="text-[11px] text-slate-500">{new Date(r.created_at).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })}{r.catatan_admin ? ` • ${r.catatan_admin}` : ""}</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded border flex items-center gap-1 shrink-0 ${cls}`}><Icon className="w-3 h-3" /> {label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
